import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';

import { userIdSelector } from '../../app/main/mainSlice';
import { LAST_RATED_SESSION } from '../../app/graphql/queries';
import { useAppSelector } from '../../app/store/hooks';
import { APP_RATING_POSTED_KEY } from '../../app/constants/constants';
import { hasPositiveLatestSession } from '../../utils/appRating';
import {
  getLocalStorage,
  setLocalStorage,
} from '../../utils/localStorageUtils';

export function useAppRatingPromptGate(): void {
  const userId = useAppSelector(userIdSelector);

  const [hasPostedRating, setHasPostedRating] = useState(
    () => getLocalStorage(APP_RATING_POSTED_KEY) === 'true'
  );

  const shouldRun = useMemo(
    () => !!userId && !hasPostedRating,
    [userId, hasPostedRating]
  );

  const [getLastRatedSession, { data, loading }] = useLazyQuery(
    LAST_RATED_SESSION,
    {
      fetchPolicy: 'network-only',
    }
  );

  const attemptedRef = useRef(false);
  const queryTriggeredRef = useRef(false);
  const isShowingRatingRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.onAppRatingPosted = (success: boolean) => {
      if (success) {
        setLocalStorage(APP_RATING_POSTED_KEY, 'true');
        setHasPostedRating(true);
        attemptedRef.current = true;
        isShowingRatingRef.current = false;
      }
    };

    return () => {
      window.onAppRatingPosted = undefined;
    };
  }, []);

  const showRatingPrompt = useCallback(() => {
    if (isShowingRatingRef.current || hasPostedRating || attemptedRef.current) {
      return;
    }

    isShowingRatingRef.current = true;
    attemptedRef.current = true;

    try {
      if (typeof window?.showAppRating === 'function') {
        window.showAppRating();
        return;
      }

      const iosBridge = window.webkit?.messageHandlers?.web;
      if (iosBridge?.postMessage) {
        iosBridge.postMessage({ action: 'show-app-rating' });
        return;
      }

      isShowingRatingRef.current = false;
      attemptedRef.current = false;
    } catch {
      isShowingRatingRef.current = false;
      attemptedRef.current = false;
    }
  }, [hasPostedRating]);

  useEffect(() => {
    if (!shouldRun || attemptedRef.current || hasPostedRating) {
      return;
    }

    if (!queryTriggeredRef.current && userId && !loading && !data) {
      queryTriggeredRef.current = true;
      getLastRatedSession({
        variables: { userId },
      });
      return;
    }

    if (queryTriggeredRef.current && !loading && data) {
      const isPositive = hasPositiveLatestSession(data?.Sessions_DB);

      if (isPositive) {
        showRatingPrompt();
      } else {
        attemptedRef.current = true;
      }
    }
  }, [
    shouldRun,
    userId,
    loading,
    data,
    hasPostedRating,
    getLastRatedSession,
    showRatingPrompt,
  ]);
}
